/**
 * Pure logic behind the account top-up ("Recharge Credit") flow — TV-2025.
 *
 * The flow does not talk to the mono API at all. It drives the **legacy
 * Webinterface** JSON endpoints under `/{eur|int}/order/payment_api/`, which are
 * the same ones the CakePHP panel's own Vue modal uses, with `mode: 'recharge'`
 * already a first-class case there. The money is collected at Adyen/Lago and
 * booked by the Webinterface exactly as it is today; for a migrated customer its
 * `BalanceForwarder` then records it on Chargebee under `topup:<gateway id>`.
 * Nothing in mono books anything — see `plans/TV-2025-implementation-plan.md` in
 * the orchestration repo.
 *
 * That works because the website and the Webinterface are served from **one
 * origin** (the edge sends `/{eur|int}/...` to CakePHP and the locale-prefixed
 * routes here), so these are same-origin requests carrying the customer's own
 * session. Cross-origin is refused outright by `_isValidOrigin()` over there, so
 * none of this survives the website moving to its own hostname.
 *
 * Everything in this file is a pure function so it can be tested without a
 * browser, a session or a payment provider.
 */

import { STRIPE_KEY_QUERY } from '~/helpers/stripeKey';

/** Region prefix the legacy routes are mounted under. */
export type RechargeRegion = 'eur' | 'int';

/** `mode` on the legacy payment API. We only ever send `recharge`. */
export const RECHARGE_MODE = 'recharge';

/**
 * One row of the `POST methods` response. `sort` really is a string and `id` is
 * really `0` for the Lago card entry — both quoted from a live response, not
 * assumed.
 */
export type RechargeMethod = {
  id: number;
  name: string;
  gateway: string;
  type: string;
  value: string;
  sort: string;
  supports_recurring?: boolean;
};

/** The Adyen Web bootstrap the same response carries. */
export type RechargeSdkData = {
  // Singular `Method` — this is the server's spelling
  // (`PaymentApiComponent::getPaymentSDKData()`), not a typo for the Adyen
  // SDK's own `paymentMethodsResponse` option.
  paymentMethodResponse: { paymentMethods: Array<Record<string, unknown>> };
  clientKey: string;
  locale: string;
  price: { amount: string; currency: string };
  env: string;
};

export type RechargeMethodsResponse = {
  methods: RechargeMethod[];
  sdk: RechargeSdkData;
};

export type RechargeMinimum = { amount: number; currency: string };

/** `POST createPayment` answers this shape. */
export type CreatePaymentResponse = {
  resultCode: string;
  paymentId?: string;
  action?: Record<string, unknown> | null;
  redirectUrl?: string | null;
  mode?: string;
};

export const GATEWAY_ADYEN = 'adyen';
export const GATEWAY_LAGO = 'lago';
/** Adyen's card method. We never render it — see `shouldRefetchMethods`. */
export const ADYEN_CARD_TYPE = 'scheme';
export const ADYEN_PAYPAL_TYPE = 'paypal';

// A QR action waits on the page for the shopper's phone; every other action leaves the page or ends.
export const isQrCodeAction = (action: Record<string, unknown>): boolean =>
  action.type === 'qrCode';

// ---------------------------------------------------------------------------
// URLs
// ---------------------------------------------------------------------------

/**
 * Relative, always. An absolute URL would make these cross-origin the moment
 * the host differs, and the Webinterface answers a foreign `Origin` on a
 * non-GET with 405 rather than a missing CORS header.
 */
export const paymentApiUrl = (region: RechargeRegion, action: string): string =>
  `/${region}/order/payment_api/${action}`;

export const lagoApiUrl = (region: RechargeRegion, action: string): string =>
  `/${region}/payment/lago/${action}`;

export const stripeApiUrl = (region: RechargeRegion, action: string): string =>
  `/${region}/payment/stripe/${action}`;

export const PAYMENTS_PATH = '/account/payments';
export const PAYMENTS_BALANCE_ANCHOR = 'balance';

/** Where the recharge surface lives. Kept in one place — it is also the return target. */
export const rechargePath = (locale: string): string =>
  `/${locale}/account/recharge`;

/**
 * The `return_url` handed to `createPayment`, absolute because the API validates
 * it with `filter_var(..., FILTER_VALIDATE_URL)`.
 *
 * **It must not carry a query string.** `PaymentApiComponent::modifyRedirectUrl()`
 * appends the payment id with
 * `http_build_query(parse_url($url, PHP_URL_QUERY) ?? [] + [...])` — where the
 * left operand of `??` is a *string*. With a query present that reaches
 * `http_build_query()` as a string, which returns nothing, and the `?pid=` is
 * dropped silently: the customer returns from the provider and nothing here can
 * tell which payment they returned from. With no query the null branch is taken
 * and the id is appended correctly.
 *
 * It is also why the return target is the bare recharge page: the edge matches
 * `^/[a-z]{2}/account/recharge$` anchored, on the path only, so the `?pid=` and
 * `?redirectResult=` that come back do not affect routing.
 */
export const rechargeReturnUrl = (origin: string, locale: string): string =>
  `${origin.replace(/\/+$/, '')}${rechargePath(locale)}`;

/**
 * The return URL for the **Lago/Stripe** arm, which unlike the Adyen one MAY
 * carry a query — and has to.
 *
 * The no-query rule above is about `createPayment`: it is the Webinterface that
 * mangles a query when it appends `?pid=`. This URL never goes near it. Stripe
 * is handed it directly for a 3DS challenge and appends its own
 * `setup_intent`, `setup_intent_client_secret` and `redirect_status` — none of
 * which say what the customer was topping up.
 *
 * So the amount travels here. Without it the customer completes the 3DS
 * challenge, returns with a perfectly good SetupIntent, and the page has nothing
 * to charge: `resumeAfterRedirect` reports a failure for a payment that was
 * never attempted. The fraction travels too, because the subunit conversion
 * Lago's `makePayment` needs is a 100x error in the wrong currency and the
 * customer record may not be loaded yet when the browser lands back here.
 */
export const lagoReturnUrl = (
  origin: string,
  locale: string,
  params: { amount: number; fraction: number },
): string => {
  const query = new URLSearchParams({
    amount: String(params.amount),
    fraction: String(params.fraction),
  });
  return `${rechargeReturnUrl(origin, locale)}?${query.toString()}`;
};

// ---------------------------------------------------------------------------
// Amounts
// ---------------------------------------------------------------------------

/**
 * Parses what the user typed. Accepts a comma as the decimal separator because
 * the German and French locales type one, and the legacy input does too.
 * Returns null for anything that is not a finite positive number.
 */
export const parseRechargeAmount = (raw: string | number): number | null => {
  if (typeof raw === 'number') {
    return Number.isFinite(raw) && raw > 0 ? raw : null;
  }
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '') return null;
  // Reject "1.2.3", "1e5", "abc" and anything with stray characters — Number()
  // alone accepts exponent notation and leading `+`, which the API would then
  // reject server-side with a less useful message.
  if (!/^\d*\.?\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value > 0 ? value : null;
};

/**
 * Keeps the amount field to what the customer can actually be charged, as they
 * type: digits, at most one decimal separator, and at most the currency's own
 * number of decimals.
 *
 * The legacy modal gets this from the browser — `BaseRechargeModal.vue` binds a
 * `BaseNumberInputElement`, so a letter or a second separator never reaches the
 * model, and it then truncates the decimals with a regex of its own. Our field
 * is a plain text input (`FormText` has no `number` type), so nothing stopped
 * `1.200,60` — the shape the balance is DISPLAYED in — from being typed and
 * then refused as invalid, which is the confusing part: the customer copied
 * what we showed them (TV-2025, QA finding 13).
 *
 * Mangling is deliberately visible rather than silent: the field shows exactly
 * the figure that will be charged, and the Pay button repeats it formatted, so
 * a dropped thousands separator is on screen before any money moves.
 *
 * A comma is kept as a separator, not stripped — German and French customers
 * type one and `parseRechargeAmount` already accepts it.
 */
export const sanitiseAmountInput = (raw: string, fraction = 2): string => {
  let seenSeparator = false;
  let decimals = 0;
  let out = '';

  for (const char of raw) {
    if (char >= '0' && char <= '9') {
      // Drop digits past the currency's precision instead of letting them
      // push the value around when `normaliseAmount` rounds later.
      if (seenSeparator) {
        if (decimals >= fraction) continue;
        decimals += 1;
      }
      out += char;
      continue;
    }
    // A second separator is a thousands separator, or a typo. Either way the
    // number it would make is not the number the customer means.
    if ((char === '.' || char === ',') && !seenSeparator) {
      // A zero-decimal currency has no fraction to keep, so the separator
      // ends the amount rather than being dropped: skipping only the
      // separator would glue the decimals onto the integer, and `1200.00`
      // would charge 120000. Truncating matches the legacy modal, whose
      // regex keeps `{0,subunit}` decimals — none, here.
      if (fraction === 0) break;
      seenSeparator = true;
      out += char;
    }
  }

  return out;
};

/**
 * i18n key for an amount that cannot be submitted, or null when it can.
 *
 * Only the amount's *shape* is checked here, never its size. The floor is per
 * method (`PaymentComponent::createPayment` refuses below
 * `payment_methods.minimum_transaction`) and the server already applies it:
 * `methods` filters the list by `minimum_transaction <= amount`, so an amount
 * too low for everything comes back as an empty list and the panel says so.
 *
 * Refusing here against `minimumTransaction` would be wrong, because that
 * endpoint is `getHighestMinimumTransaction()` — the MAX across live methods,
 * not the MIN. In EUR that is 0.90 (directEbanking/ideal/paysafecard) while card
 * and PayPal accept 0.44, so a client-side floor rejects amounts those two
 * gladly take. The legacy modal does not do this either: `BaseRechargeModal.vue`
 * rejects only `amount <= 0` and lets the empty `methods` response speak.
 *
 * There is deliberately no maximum — none exists in any layer of the legacy
 * stack either.
 */
export const rechargeAmountError = (
  raw: string | number,
  fraction = 2,
): string | null => {
  const amount = parseRechargeAmount(raw);
  if (amount == null) return 'account/recharge/error/amount_invalid';
  // Anything that rounds to nothing at the currency's own precision is not a
  // payable amount — 0.0000001 EUR is zero cents. Catching it here also keeps a
  // zero out of `methods`, where INT's `directEbanking` row has a
  // `minimum_transaction` of 0.00 and would otherwise offer a tile for a
  // payment of nothing.
  if (normaliseAmount(amount, fraction) === 0) {
    return 'account/recharge/error/amount_invalid';
  }
  return null;
};

/**
 * Rounds a typed amount to the currency's own precision.
 *
 * Both arms have to agree on the figure. `createPayment` is sent **major**
 * units and Lago's `makePayment` is sent **subunits**, and the two are derived
 * separately — so an amount carrying more decimals than the currency has (a
 * pasted `1.005`, say) can be rounded one way by PHP's `GPMoney` and the other
 * way here, and the ledger and the gateway then disagree by a fraction of a
 * cent. Canonicalising once, at the point the customer's input becomes a
 * number, removes the class of problem rather than patching each arm.
 */
export const normaliseAmount = (amount: number, fraction = 2): number =>
  shiftedRound(amount, fraction) / 10 ** fraction;

// No grouping, so `sanitiseAmountInput` keeps the figure as it is.
export const formatAmountInput = (
  amount: number,
  fraction: number,
  locale: string,
): string =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: fraction,
    maximumFractionDigits: fraction,
    useGrouping: false,
    numberingSystem: 'latn',
  }).format(normaliseAmount(amount, fraction));

/** Caller amount first, else the open invoices (negative balance, minor units) but at least the minimum. */
export const initialRechargeAmount = (
  prefill: number | null | undefined,
  minimum: number | null,
  negativeBalance: { amount: bigint | string; fraction: number } | null,
): number | null => {
  if (prefill != null) return prefill;
  if (negativeBalance == null) return minimum;
  const owed =
    Number(-BigInt(negativeBalance.amount)) / 10 ** negativeBalance.fraction;
  return minimum == null ? owed : Math.max(owed, minimum);
};

/** Major units → minor units, for Lago's `makePayment?amount=` which wants subunits. */
export const toSubunits = (amount: number, fraction = 2): number =>
  shiftedRound(amount, fraction);

/**
 * `Math.round(amount * 10 ** fraction)`, done on the decimal representation
 * rather than the binary one.
 *
 * `1.005` is not representable: the nearest double is 1.00499999999999989, so
 * `1.005 * 100` is `100.49999999999999` and rounds DOWN. A customer who types
 * 1.005 is then charged 1.00 — a silent cent in their favour, and a surprise
 * either way. Re-parsing through an exponent moves the point in decimal, where
 * `1.005e2` is exactly 100.5 and rounds half-up as anyone would expect.
 *
 * Both callers go through this so the major-unit figure sent to `createPayment`
 * and the subunit figure sent to Lago can never round in different directions.
 */
const shiftedRound = (amount: number, fraction: number): number => {
  // `${amount}` is itself exponent notation below 1e-6 and at or above 1e21, and
  // appending a second exponent to one gives `"1e-7e2"`, which parses to NaN.
  // That NaN survives all the way out: `JSON.stringify` turns it into
  // `{"amount":null}` and `methods` answers 500, because `getPaymentSDKData()`
  // takes a non-nullable `GPMoney`. So fold `fraction` INTO the existing
  // exponent rather than after it.
  const [mantissa, exponent] = String(amount).split('e');
  return Math.round(
    Number(
      exponent === undefined
        ? `${mantissa}e${fraction}`
        : `${mantissa}e${Number(exponent) + fraction}`,
    ),
  );
};

// ---------------------------------------------------------------------------
// Method tiles
// ---------------------------------------------------------------------------

export const isAdyenMethod = (method: RechargeMethod): boolean =>
  method.gateway === GATEWAY_ADYEN;

export const isLagoMethod = (method: RechargeMethod): boolean =>
  method.gateway === GATEWAY_LAGO;

const isAdyenCard = (method: RechargeMethod): boolean =>
  method.gateway === GATEWAY_ADYEN && method.type === ADYEN_CARD_TYPE;

/**
 * Whether the `methods` response has to be asked for again.
 *
 * Credit card is Lago via Stripe — `Lago.percentage` is 100, so
 * `PaymentApiController::methods()` swaps Adyen's `scheme` row for a Lago one.
 * But `PaymentComponent::shouldSwitchToLago()` is `rand(0, 100) < 100`, so
 * roughly one call in 101 rolls a literal 100 and the Adyen card row comes back
 * instead. We implement no Adyen card form (TV-2025 decision 1), so rather than
 * render a tile that cannot be paid, re-roll once: the odds of two in a row are
 * about 1 in 10,000.
 *
 * Deliberately one retry and not a loop — a persistent `scheme` row means
 * something changed server-side (`Lago.percentage` lowered, or the customer is
 * pinned off Lago), and spinning forever would hide that.
 */
export const shouldRefetchMethods = (methods: RechargeMethod[]): boolean =>
  methods.some(isAdyenCard);

/**
 * Tiles to render, in the server's order. Adyen's card row is dropped if it
 * survived the retry above, so the customer never sees a tile with no form
 * behind it.
 */
export const visibleMethods = (methods: RechargeMethod[]): RechargeMethod[] =>
  [...methods]
    .filter((method) => !isAdyenCard(method))
    .sort((a, b) => Number(a.sort) - Number(b.sort));

/** Stable key for a tile. `id` is 0 for the Lago row, so it cannot be the key. */
export const methodKey = (method: RechargeMethod): string =>
  method.value || `${method.gateway}.${method.type}`;

// The server's names leak implementation, so known methods are translated and new ones keep the server name.
export const methodLabel = (
  method: RechargeMethod,
  t: (key: string) => string,
): string => {
  if (isLagoMethod(method)) return t('account/recharge/method/card');
  const key = `account/recharge/method/${method.type}`;
  const translated = t(key);
  return translated === key ? method.name : translated;
};

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/* eslint-disable camelcase -- the legacy payment API's wire fields are snake_case */
export type CreatePaymentBody = {
  mode: string;
  method_id: number;
  price: { amount: number; currency: string };
  return_url: string;
  payload?: unknown;
};

/**
 * `createPayment`'s body. `price.amount` is decimal **major** units — the API
 * runs it through `filter_var(FILTER_VALIDATE_FLOAT)` and `GPMoney::createMoney`
 * — while Lago's separate `makePayment` wants subunits. The two disagree on
 * purpose; see `toSubunits`.
 */
export const buildCreatePaymentBody = (params: {
  methodId: number;
  amount: number;
  currency: string;
  returnUrl: string;
  payload?: unknown;
}): CreatePaymentBody => ({
  mode: RECHARGE_MODE,
  method_id: params.methodId,
  price: { amount: params.amount, currency: params.currency },
  return_url: params.returnUrl,
  ...(params.payload === undefined ? {} : { payload: params.payload }),
});
/* eslint-enable camelcase */

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

/**
 * Payment states the legacy API reports. `checkout` is not a state — it is
 * "the provider wants another step", carrying an Adyen action for the SDK.
 */
export const RESULT_SUCCESS = 'success';
export const RESULT_PENDING = 'pending';
export const RESULT_FAILURE = 'failure';
export const RESULT_CHECKOUT = 'checkout';

export type RechargeOutcome = 'success' | 'pending' | 'failure';

/**
 * Maps a payment status to what the customer is told.
 *
 * `pending` is not a failure and must not read as one: SOFORT and Paysafecard
 * routinely settle asynchronously, and the Adyen webhook credits the balance
 * server-side whether or not this browser is still open. Telling someone their
 * top-up failed while the money is on its way produces a duplicate payment.
 */
export const outcomeFromResultCode = (
  resultCode: string | null | undefined,
): RechargeOutcome => {
  switch ((resultCode ?? '').toLowerCase()) {
    case RESULT_SUCCESS:
    case 'authorised':
    case 'authorized':
      return 'success';
    case RESULT_PENDING:
    case 'received':
      return 'pending';
    default:
      return 'failure';
  }
};

export const outcomeMessageKey = (outcome: RechargeOutcome): string => {
  switch (outcome) {
    case 'success':
      return 'account/recharge/result/success';
    case 'pending':
      return 'account/recharge/result/pending';
    default:
      return 'account/recharge/result/failure';
  }
};

/**
 * The query the provider sends the customer back with.
 *
 * `pid` is ours, appended by `modifyRedirectUrl`. `redirectResult` is Adyen's,
 * and is what `additionalPaymentDetails` needs to finish the payment. A `pid`
 * with no `redirectResult` is a return we cannot finalise from here — the
 * webhook still will, so it is reported as pending rather than failed.
 */
export type RechargeReturnQuery = {
  paymentId: string;
  redirectResult: string | null;
};

const firstValue = (value: unknown): string | null => {
  if (Array.isArray(value)) return firstValue(value[0]);
  if (typeof value === 'string' && value !== '') return value;
  return null;
};

export const parseReturnQuery = (
  query: Record<string, unknown>,
): RechargeReturnQuery | null => {
  const paymentId = firstValue(query.pid);
  if (paymentId == null) return null;
  return { paymentId, redirectResult: firstValue(query.redirectResult) };
};

/**
 * The amount a magic link asks to be topped up.
 *
 * Both spellings are accepted on purpose. `?amount=` is what this page's own
 * links use; `?action=showCredit&amount=` is the legacy contract that
 * `DashboardsController::getCredit()` redirects the recharge mails into, and
 * which `BaseLayout.vue` reads over in the CakePHP panel. Accepting it here is
 * what lets that mail point at mono without the mail template changing shape.
 */
/**
 * The currency precision a Lago 3DS return carries back — see `lagoReturnUrl`.
 * Bounded rather than trusted: it drives a subunit multiplication, so a junk
 * value in a URL the customer can edit would be a 100x charge.
 */
export const fractionFromQuery = (
  query: Record<string, unknown>,
): number | null => {
  const raw = firstValue(query.fraction);
  if (raw == null) return null;
  const value = Number(raw);
  return Number.isInteger(value) && value >= 0 && value <= 4 ? value : null;
};

export const prefillAmountFromQuery = (
  query: Record<string, unknown>,
): number | null => {
  const raw = firstValue(query.amount);
  if (raw == null) return null;
  return parseRechargeAmount(raw);
};

// Provider parameters the payments page handles once and then drops from the URL.
export const RETURN_QUERY_KEYS = [
  'pid',
  'redirectResult',
  'setup_intent',
  'setup_intent_client_secret',
  'redirect_status',
  STRIPE_KEY_QUERY,
] as const;

export const stripeReturnSecret = (
  query: Record<string, unknown>,
): string | null => firstValue(query.setup_intent_client_secret);

// A recharge mail or a provider return; both open the top-up form.
export const hasRechargeQuery = (query: Record<string, unknown>): boolean =>
  prefillAmountFromQuery(query) != null ||
  parseReturnQuery(query) != null ||
  stripeReturnSecret(query) != null;

// The query is kept as is, because provider returns and recharge mails still land on the recharge URL.
export const rechargeRedirectLocation = <Q>(
  paymentsPath: string,
  query: Q,
): { path: string; query: Q; hash: string } => ({
  path: paymentsPath,
  query,
  hash: `#${PAYMENTS_BALANCE_ANCHOR}`,
});
